import { ButtonHTMLAttributes, forwardRef, Fragment, isValidElement, ReactNode } from 'react';

import { IconSlot, StyledButton } from './Button.styled';
import { ButtonSize, ButtonType } from './spec';

export type { ButtonSize, ButtonType } from './spec';

// `htmlType` carries the HTML attribute, because "type" is the design's name for the variant.
type BaseProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> & {
  variant?: ButtonType;
  size?: ButtonSize;
  htmlType?: 'button' | 'submit' | 'reset';
};

// A button's label or icon may not be null, undefined or a boolean: the button would render empty,
// with no accessible name (labelled) or nothing to see (icon-only). TypeScript cannot refuse an
// empty string, whitespace, or a list or fragment of only empty parts (`[null]`, `<></>`), so the
// component reports those in development (`reportEmpty`). An element that renders nothing at run
// time (a component returning null) cannot be seen from here.
type Rendered = Exclude<ReactNode, null | undefined | boolean>;

const isEmpty = (node: ReactNode): boolean =>
  node === null ||
  node === undefined ||
  typeof node === 'boolean' ||
  (typeof node === 'string' && node.trim() === '') ||
  (Array.isArray(node) && node.every(isEmpty)) ||
  (isValidElement(node) &&
    node.type === Fragment &&
    isEmpty((node.props as { children?: ReactNode }).children));

function reportEmpty(problem: string) {
  if (!import.meta.env.DEV) return;
  // eslint-disable-next-line no-console -- a development-only report, like React's own prop warnings
  console.error(`Button: ${problem}, so the button has no accessible name or content`);
}

// An icon-only button has no visible text, so its accessible name is required by type.
// The `never` keys keep the two shapes exclusive: no children dropped, no `icon` on the DOM.
type IconOnlyProps = {
  iconOnly: true;
  icon: Rendered;
  'aria-label': string;
  iconBefore?: never;
  iconAfter?: never;
  children?: never;
};

type LabelledProps = {
  iconOnly?: false;
  icon?: never;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  children: Rendered;
};

export type ButtonProps = BaseProps & (IconOnlyProps | LabelledProps);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'default', size = 'M', htmlType = 'button', ...props }, ref) => {
    const styling = { $variant: variant, $size: size };

    if (props.iconOnly) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept off the DOM
      const { iconOnly, icon, iconBefore, iconAfter, ...rest } = props;
      if (isEmpty(icon)) reportEmpty('the icon is empty');
      // A plain-JavaScript caller can omit the label the type requires; it must not crash the button.
      const label: unknown = props['aria-label'];
      if (typeof label !== 'string' || !label.trim()) reportEmpty('the aria-label is empty');

      return (
        // eslint-disable-next-line react/button-has-type -- htmlType is constrained to the three valid values
        <StyledButton ref={ref} type={htmlType} {...styling} $iconOnly {...rest}>
          <IconSlot $size={size}>{icon}</IconSlot>
        </StyledButton>
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept off the DOM
    const { iconOnly, icon, iconBefore, iconAfter, children, ...rest } = props;
    if (isEmpty(children)) reportEmpty('the label is empty');

    return (
      // eslint-disable-next-line react/button-has-type -- htmlType is constrained to the three valid values
      <StyledButton ref={ref} type={htmlType} {...styling} $iconOnly={false} {...rest}>
        {iconBefore && <IconSlot $size={size}>{iconBefore}</IconSlot>}
        {children}
        {iconAfter && <IconSlot $size={size}>{iconAfter}</IconSlot>}
      </StyledButton>
    );
  },
);

Button.displayName = 'Button';
